import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { aiDocumentSplitApi, taskApi } from 'app/services/api';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import {
  CancelIcon,
  OpenIcon,
  PlusIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getDefaultErrorMessage } from 'utils/axios';
import { jsonStringify } from 'utils/json';
import { logger } from 'utils/logger';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  editDocumentAssociationsAction,
  getDocumentAssociationsAction,
} from 'store/documents/actions';
import { selectDocumentFilledExtractionFields } from 'store/documents/selectors';
import { showErrorMessageAction, showMessageAction } from 'store/global/actions';
import {
  getAssetsAction,
  getBorrowersTableDataAction,
  getFinancialInfoPropertiesAction,
  getIncomesAction,
  getLiabilitiesAction,
} from 'store/loanApplications/actions';
import { selectDocumentAssociationsMulti } from 'store/loanApplications/selectors';

import TaskLinkGroup from './components/TaskLinkGroup';
import { ICON_SIZE_MD, ICON_SIZE_SM, LINK_ENTITY_TYPE_CONFIG } from './DocumentLinksTab.constants';
import {
  aiSuggestBannerStyles,
  aiSuggestDismissButtonStyles,
  aiSuggestLabelStyles,
  aiSuggestLinkButtonStyles,
  aiSuggestMidStyles,
  aiSuggestTileStyles,
  autocompleteRowStyles,
  cardIconButtonStyles,
  cardOpenIconButtonStyles,
  columnLayoutStyles,
  emptySlotStyles,
  footerStyles,
  getCardIconTileStyles,
  getGroupIconTileStyles,
  groupAddButtonStyles,
  groupCountBadgeStyles,
  groupHeaderStyles,
  groupWrapperStyles,
  linkActionsBoxStyles,
  linkCardStyles,
  linkNameCellStyles,
  linkNameTypographyStyles,
  scrollableColumnStyles,
  sectionHeaderCountStyles,
  sectionHeaderStyles,
} from './DocumentLinksTab.styles';
import { DocumentLinksTabTestIds } from './DocumentLinksTab.testIds';
import {
  getLinksFieldsToSend,
  getTaskLinkCount,
  resolveEntityColor,
} from './DocumentLinksTab.utils';

/**
 * Links tab of DocumentInspector.
 *
 * View mode: entity-type groups, each with a colored tile, count badge, and a per-group
 * "+ Add" button; each linked entity card shows an open icon and a CDS unlink icon button.
 * Edit mode: one AutocompleteCDS multiple={true} per entity type.
 *
 * AI suggestions: when extracted fields exist, calls POST document-entity-matches (DEV-4189)
 * and renders a dismissible violet suggestion banner per match above the entity groups.
 */
const DocumentLinksTab = ({
  documentId,
  documentTypeId = null,
  definitionLeaves = null,
  applicationId: applicationIdFromProp,
  taskId = null,
  onOpenEntity,
}) => {
  const { applicationId: applicationIdFromUrl } = useParams();
  const applicationId = applicationIdFromProp ?? applicationIdFromUrl;

  const dispatch = useDispatch();

  const { isManageAllowed: isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_DOCUMENT_ASSOCIATIONS),
  );

  const { documentAssociationsOptions, documentAssociationsInitialFormValues } = useSelector(
    selectDocumentAssociationsMulti,
  );

  const extractionFieldsResults = useSelector(selectDocumentFilledExtractionFields, _.isEqual);

  const [form, setForm] = useState(documentAssociationsInitialFormValues);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [dismissedSuggestionKeys, setDismissedSuggestionKeys] = useState([]);
  const [taskName, setTaskName] = useState(null);
  const latestTaskIdRef = useRef(taskId);

  const loadAssociations = useCallback(() => {
    setIsLoading(true);

    const associationsPromise = dispatch(
      getDocumentAssociationsAction({ documentId, silent: true }),
    );

    const optionsPromise = Promise.all([
      dispatch(getBorrowersTableDataAction({ applicationId, silent: true })),
      dispatch(getIncomesAction({ applicationId, silent: true })),
      dispatch(getAssetsAction({ applicationId, silent: true })),
      dispatch(getFinancialInfoPropertiesAction({ applicationId, silent: true })),
      dispatch(getLiabilitiesAction({ applicationId, silent: true })),
    ]);

    Promise.all([associationsPromise, optionsPromise])
      .catch(err => {
        logger.error(err);
        dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
      })
      .finally(() => setIsLoading(false));
  }, [applicationId, dispatch, documentId]);

  useEffectSafeStrictMode(() => {
    loadAssociations();
  }, [loadAssociations]);

  useEffect(() => {
    setForm(documentAssociationsInitialFormValues);
  }, [documentAssociationsInitialFormValues]);

  useEffectSafeStrictMode(() => {
    latestTaskIdRef.current = taskId;
    setTaskName(null);

    if (!getTaskLinkCount(taskId)) {
      return;
    }

    taskApi
      .getTaskById({ taskId })
      .then(({ data }) => {
        // The tab is not remounted per document, so a slower response for the previous document must not win.
        if (latestTaskIdRef.current === taskId) {
          setTaskName(data?.name ?? null);
        }
      })
      .catch(err => logger.error(err));
  }, [taskId]);

  useEffectSafeStrictMode(() => {
    if (_.isEmpty(extractionFieldsResults) || _.isNil(documentTypeId)) {
      return;
    }

    if (_.isEmpty(definitionLeaves)) {
      return;
    }

    const fieldMap = _.keyBy(definitionLeaves, 'id');
    // The endpoint expects extractedFields as a JSON-serialised object whose keys are
    // field names and values are the extracted text (matches the BE `string ExtractedFields`).
    // Results also carry rows for container nodes, which are not leaves and so resolve to no
    // name; drop them rather than keying them by their GUID.
    const fieldEntries = extractionFieldsResults
      .map(result => [fieldMap[result.documentExtractionFieldId]?.name, result.value ?? null])
      .filter(([name, value]) => !_.isNil(name) && !_.isNil(value));

    if (_.isEmpty(fieldEntries)) {
      return;
    }

    aiDocumentSplitApi
      .proposeEntityMatches({
        loanApplicationId: applicationId,
        extractedFields: jsonStringify(_.fromPairs(fieldEntries)),
        documentTypeId,
      })
      .then(({ data }) => {
        const suggestions = LINK_ENTITY_TYPE_CONFIG.flatMap(({ type }) => {
          const entityKey = type;

          return (data[entityKey] ?? []).map(match => ({
            key: `${entityKey}-${match.entityId}`,
            entityType: entityKey,
            entityId: match.entityId,
            displayName: match.displayName,
            matchRationale: match.matchRationale,
          }));
        });

        setAiSuggestions(suggestions);
      })
      .catch(err => {
        logger.error(err);
      });
  }, [applicationId, definitionLeaves, documentTypeId, extractionFieldsResults]);

  const onDismissSuggestion = useCallback(e => {
    const key = e.currentTarget.getAttribute('data-suggestion-key');

    setDismissedSuggestionKeys(prev => [...prev, key]);
  }, []);

  const onAcceptSuggestion = useCallback(
    e => {
      const key = e.currentTarget.getAttribute('data-suggestion-key');
      const entityType = e.currentTarget.getAttribute('data-entity-type');
      const entityId = e.currentTarget.getAttribute('data-entity-id');
      const entityName = e.currentTarget.getAttribute('data-entity-name');

      const newItem = { value: entityId, name: entityName };
      const updatedForm = {
        ...form,
        [entityType]: _.uniqBy([...(form[entityType] ?? []), newItem], 'value'),
      };

      setIsSaving(true);
      setDismissedSuggestionKeys(prev => [...prev, key]);

      const fields = getLinksFieldsToSend(updatedForm);

      dispatch(editDocumentAssociationsAction({ documentId, ...fields }))
        .then(() => {
          setForm(updatedForm);
          loadAssociations();
          dispatch(showMessageAction({ message: 'Link added', variant: 'success' }));
        })
        .catch(err => {
          logger.error(err);
          dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
        })
        .finally(() => {
          setIsSaving(false);
        });
    },
    [dispatch, documentId, form, loadAssociations],
  );

  const onAutocompleteChange = useCallback(
    type => (event, value) => {
      setForm(prev => ({ ...prev, [type]: value }));
    },
    [],
  );

  const onEditClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const onCancelClick = useCallback(() => {
    setForm(documentAssociationsInitialFormValues);
    setIsEditing(false);
  }, [documentAssociationsInitialFormValues]);

  const onSaveClick = useCallback(async () => {
    setIsSaving(true);

    try {
      const fields = getLinksFieldsToSend(form);

      await dispatch(editDocumentAssociationsAction({ documentId, ...fields }));

      loadAssociations();
      setIsEditing(false);

      dispatch(showMessageAction({ message: 'Document links updated', variant: 'success' }));
    } catch (err) {
      logger.error(err);
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, documentId, form, loadAssociations]);

  const onUnlinkClick = useCallback(e => {
    const entityType = e.currentTarget.getAttribute('data-entity-type');
    const itemValue = e.currentTarget.getAttribute('data-item-value');

    setForm(prev => ({
      ...prev,
      [entityType]: (prev[entityType] ?? []).filter(o => o.value !== itemValue),
    }));
  }, []);

  const onOpenEntityClick = useCallback(
    e => {
      const entityType = e.currentTarget.getAttribute('data-entity-type');
      const itemValue = e.currentTarget.getAttribute('data-item-value');

      onOpenEntity?.({ type: entityType, id: itemValue });
    },
    [onOpenEntity],
  );

  const totalLinkCount = useMemo(
    () =>
      LINK_ENTITY_TYPE_CONFIG.reduce((acc, { type }) => acc + (form[type]?.length ?? 0), 0) +
      getTaskLinkCount(taskId),
    [form, taskId],
  );

  const visibleSuggestions = useMemo(
    () => aiSuggestions.filter(s => !dismissedSuggestionKeys.includes(s.key)),
    [aiSuggestions, dismissedSuggestionKeys],
  );

  const entityColorMap = useMemo(
    () =>
      Object.fromEntries(
        LINK_ENTITY_TYPE_CONFIG.map(({ type, colorName }) => [type, resolveEntityColor(colorName)]),
      ),
    [],
  );

  if (isEditing) {
    return (
      <Box sx={columnLayoutStyles}>
        <Box sx={scrollableColumnStyles}>
          <Box sx={sectionHeaderStyles}>
            <Typography color="text.secondary" variant="bold12">
              Edit linked entities
            </Typography>
          </Box>
          {LINK_ENTITY_TYPE_CONFIG.map(({ type, editLabel }) => (
            <Box key={type} sx={autocompleteRowStyles}>
              <AutocompleteCDS
                multiple
                dataTestId={DocumentLinksTabTestIds.getTypeSelectId(type)}
                disabled={!isEditAllowed || isSaving}
                label={editLabel}
                name={type}
                options={documentAssociationsOptions[type]}
                value={form[type]}
                onChange={onAutocompleteChange(type)}
              />
            </Box>
          ))}
        </Box>
        <Box sx={footerStyles}>
          <Button
            data-testid={DocumentLinksTabTestIds.CANCEL_BUTTON}
            disabled={isSaving}
            variant="outlined"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid={DocumentLinksTabTestIds.SAVE_BUTTON}
            disabled={isSaving}
            variant="contained"
            onClick={onSaveClick}
          >
            Save links
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={columnLayoutStyles}>
      {isLoading && (
        <Typography color="text.secondary" variant="regular12">
          Loading...
        </Typography>
      )}

      {!isLoading && (
        <Box sx={scrollableColumnStyles}>
          <Box sx={sectionHeaderStyles}>
            <Typography color="text.secondary" variant="bold12">
              Linked entities
            </Typography>
            <Box sx={sectionHeaderCountStyles}>
              <Typography color="text.secondary" variant="regular12">
                {totalLinkCount} {totalLinkCount === 1 ? 'link' : 'links'}
              </Typography>
            </Box>
          </Box>

          {visibleSuggestions.map(suggestion => (
            <Box key={suggestion.key} sx={aiSuggestBannerStyles}>
              <Box sx={aiSuggestTileStyles}>
                <svg fill="currentColor" height="16" viewBox="0 0 24 24" width="16">
                  <path d="M12 2l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" />
                </svg>
              </Box>
              <Box sx={aiSuggestMidStyles}>
                <Typography sx={aiSuggestLabelStyles} variant="bold12">
                  AI suggests · from extracted fields
                </Typography>
                <Typography variant="medium14">{suggestion.displayName}</Typography>
              </Box>
              <Box
                component="button"
                data-entity-id={suggestion.entityId}
                data-entity-name={suggestion.displayName}
                data-entity-type={suggestion.entityType}
                data-suggestion-key={suggestion.key}
                disabled={isSaving}
                sx={aiSuggestLinkButtonStyles}
                type="button"
                onClick={onAcceptSuggestion}
              >
                Link
              </Box>
              <Box
                component="button"
                data-suggestion-key={suggestion.key}
                sx={aiSuggestDismissButtonStyles}
                type="button"
                onClick={onDismissSuggestion}
              >
                ✕
              </Box>
            </Box>
          ))}

          {LINK_ENTITY_TYPE_CONFIG.map(({ type, label, emptyLabel, Icon }) => {
            const items = form[type] ?? [];
            const { bg, fg } = entityColorMap[type];

            return (
              <Box key={type} sx={groupWrapperStyles}>
                <Box sx={groupHeaderStyles}>
                  <Box sx={getGroupIconTileStyles({ bg, fg })}>
                    <Icon size={ICON_SIZE_SM} />
                  </Box>
                  <Typography variant="medium12">{label}</Typography>
                  <Box sx={groupCountBadgeStyles}>
                    <Typography variant="regular12">{items.length}</Typography>
                  </Box>
                  {isEditAllowed && (
                    <Button
                      aria-label={`Add ${label} link`}
                      data-entity-type={type}
                      data-testid={DocumentLinksTabTestIds.getAddButtonId(type)}
                      size="small"
                      sx={groupAddButtonStyles}
                      variant="text"
                      onClick={onEditClick}
                    >
                      <PlusIcon size={ICON_SIZE_SM} />
                      Add
                    </Button>
                  )}
                </Box>

                {items.map(item => (
                  <Box key={item.value} sx={linkCardStyles}>
                    <Box sx={getCardIconTileStyles({ bg, fg })}>
                      <Icon size={ICON_SIZE_MD} />
                    </Box>

                    <Box sx={linkNameCellStyles}>
                      <Typography sx={linkNameTypographyStyles} variant="medium14">
                        {item.name}
                      </Typography>
                    </Box>

                    {(onOpenEntity || isEditAllowed) && (
                      <Box sx={linkActionsBoxStyles}>
                        {onOpenEntity && (
                          <Tooltip title="Open">
                            <IconButton
                              aria-label={`Open ${item.name}`}
                              data-entity-type={type}
                              data-item-value={item.value}
                              data-testid={DocumentLinksTabTestIds.getOpenButtonId(item.value)}
                              size="small"
                              sx={cardOpenIconButtonStyles}
                              onClick={onOpenEntityClick}
                            >
                              <OpenIcon size={ICON_SIZE_MD} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {isEditAllowed && (
                          <Tooltip title="Unlink">
                            <IconButton
                              aria-label={`Unlink ${item.name}`}
                              data-entity-type={type}
                              data-item-value={item.value}
                              data-testid={DocumentLinksTabTestIds.getUnlinkButtonId(item.value)}
                              size="small"
                              sx={cardIconButtonStyles}
                              onClick={onUnlinkClick}
                            >
                              <CancelIcon size={ICON_SIZE_MD} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}

                {!items.length && (
                  <Box
                    data-testid={`empty-slot-${type}`}
                    sx={emptySlotStyles}
                    onClick={isEditAllowed ? onEditClick : undefined}
                  >
                    <Typography variant="regular12">{emptyLabel}</Typography>
                  </Box>
                )}
              </Box>
            );
          })}

          {!!getTaskLinkCount(taskId) && <TaskLinkGroup taskName={taskName} />}
        </Box>
      )}

      {isEditAllowed && (
        <Box sx={footerStyles}>
          <Button
            fullWidth
            data-testid={DocumentLinksTabTestIds.EDIT_BUTTON}
            disabled={isLoading}
            variant="outlined"
            onClick={onEditClick}
          >
            Edit links
          </Button>
        </Box>
      )}
    </Box>
  );
};

DocumentLinksTab.propTypes = {
  documentId: PropTypes.string.isRequired,
  definitionLeaves: PropTypes.arrayOf(PropTypes.object),
  documentTypeId: PropTypes.string,
  applicationId: PropTypes.string,
  taskId: PropTypes.string,
  onOpenEntity: PropTypes.func,
};

export default memo(DocumentLinksTab);
